import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  Paper,
  Typography,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  InputAdornment,
  TextField
} from '@mui/material';
import {
  CalendarToday,
  Clear,
  KeyboardArrowDown,
  Search
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomCalendar from './CustomCalendar';

const HistoryFiltersPanel = ({ 
  dateFrom, 
  dateTo, 
  onDateChange, 
  onClear,
  placeholder = "Select date range",
  searchValue = '',
  onSearchChange,
  searchPlaceholder = "Search..."
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [customDateFrom, setCustomDateFrom] = useState(dateFrom || '');
  const [customDateTo, setCustomDateTo] = useState(dateTo || '');
  const [calendarFromOpen, setCalendarFromOpen] = useState(false);
  const [calendarToOpen, setCalendarToOpen] = useState(false);
  const [calendarFromAnchor, setCalendarFromAnchor] = useState(null);
  const [calendarToAnchor, setCalendarToAnchor] = useState(null);

  // Preset date ranges
  const getPresetRanges = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);
    
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const previousMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const previousMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

    const formatDate = (date) => date.toISOString().split('T')[0];

    return [
      {
        id: 'today',
        label: t('datePicker.today', 'Today'),
        from: formatDate(today),
        to: formatDate(today)
      },
      {
        id: 'yesterday',
        label: t('datePicker.yesterday', 'Yesterday'),
        from: formatDate(yesterday),
        to: formatDate(yesterday)
      },
      {
        id: 'last7days',
        label: t('datePicker.last7Days', 'Last 7 days'),
        from: formatDate(last7Days),
        to: formatDate(today)
      },
      {
        id: 'last30days',
        label: t('datePicker.last30Days', 'Last 30 days'),
        from: formatDate(last30Days),
        to: formatDate(today)
      },
      {
        id: 'currentMonth',
        label: t('datePicker.currentMonth', 'Current month'),
        from: formatDate(currentMonthStart),
        to: formatDate(currentMonthEnd)
      },
      {
        id: 'previousMonth',
        label: t('datePicker.previousMonth', 'Previous month'),
        from: formatDate(previousMonthStart),
        to: formatDate(previousMonthEnd)
      }
    ];
  };

  // Update internal state when props change
  React.useEffect(() => {
    setCustomDateFrom(dateFrom || '');
    setCustomDateTo(dateTo || '');
    
    // Update selected preset if current dates match a preset
    const presetRanges = getPresetRanges();
    const matchingPreset = presetRanges.find(p => p.from === dateFrom && p.to === dateTo);
    setSelectedPreset(matchingPreset ? matchingPreset.id : '');
  }, [dateFrom, dateTo]);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const presetRanges = getPresetRanges();

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setCustomDateFrom(preset.from);
    setCustomDateTo(preset.to);
    onDateChange(preset.from, preset.to);
    handleClose();
  };

  const handleCustomApply = () => {
    onDateChange(customDateFrom, customDateTo);
    handleClose();
  };

  const handleClear = () => {
    setSelectedPreset('');
    setCustomDateFrom('');
    setCustomDateTo('');
    onClear();
    handleClose();
  };

  // Format display text
  const getDisplayText = () => {
    if (!dateFrom && !dateTo) {
      return placeholder;
    }
    
    const selectedPresetObj = presetRanges.find(p => 
      p.from === dateFrom && p.to === dateTo
    );
    
    if (selectedPresetObj) {
      return selectedPresetObj.label;
    }
    
    if (dateFrom && dateTo) {
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('uk-UA', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        });
      };
      
      if (dateFrom === dateTo) {
        return formatDate(dateFrom);
      }
      
      return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    }
    
    return placeholder;
  };

  return (
    <>
      {/* Custom CSS for date inputs and calendar styling */}
      <style>
        {`
          /* Global CSS for native date picker calendars - this should apply to the browser's native calendar */
          * {
            --calendar-accent-color: #00C853;
            --calendar-hover-color: #00A843;
            --calendar-background: #f0fdf4;
          }
          /* Custom calendar button styling */
          .custom-date-button .MuiButton-startIcon svg {
            color: #00C853 !important;
          }
          
          /* Material-UI overrides for green theme and rounded design */
          .MuiPickersDay-root.Mui-selected {
            background-color: #00C853 !important;
            border-radius: 8px !important;
          }
          .MuiPickersDay-root.Mui-selected:hover {
            background-color: #00A843 !important;
          }
          .MuiPickersDay-root {
            border-radius: 8px !important;
          }
          .MuiPickersDay-root:hover {
            background-color: #f0fdf4 !important;
            border-radius: 8px !important;
          }
          .MuiDateRangePickerDay-rangeIntervalDayHighlight {
            background-color: #d1fae5 !important;
          }
          
          /* Button styling */
          .MuiButton-contained {
            border-radius: 8px !important;
          }
          .MuiButton-outlined {
            border-radius: 8px !important;
          }
          .MuiButton-contained.Mui-disabled {
            background-color: #e5e7eb !important;
            color: #9ca3af !important;
            border-radius: 8px !important;
          }
          
          /* Chip styling */
          .MuiChip-root {
            border-radius: 8px !important;
          }
          
          /* Popover and Paper styling */
          .MuiPaper-root {
            border-radius: 12px !important;
          }
          
          /* TextField styling */
          .MuiOutlinedInput-root {
            border-radius: 8px !important;
          }
          .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
            border-radius: 8px !important;
          }
          
          /* Primary color overrides only for specific components */
          .MuiChip-colorPrimary {
            background-color: #00C853 !important;
            color: white !important;
          }
          .MuiChip-colorPrimary:hover {
            background-color: #00A843 !important;
          }
          
          /* Apply button inside popover should be green */
          .MuiPopover-root .MuiButton-containedPrimary {
            background-color: #00C853 !important;
            color: white !important;
          }
          .MuiPopover-root .MuiButton-containedPrimary:hover {
            background-color: #00A843 !important;
          }
          /* Ensure any contained button inside the popover has white text when enabled */
          .MuiPopover-root .MuiButton-contained:not(.Mui-disabled) {
            color: white !important;
          }
          
          /* Material-UI DatePicker global styles */
          .MuiPickersPopper-root .MuiPaper-root {
            border-radius: 12px !important;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
          }
          
          /* Calendar navigation buttons */
          .MuiPickersCalendarHeader-root .MuiIconButton-root {
            color: #00C853 !important;
          }
          .MuiPickersCalendarHeader-root .MuiIconButton-root:hover {
            background-color: #f0fdf4 !important;
          }
          
          /* Month/Year selector */
          .MuiPickersCalendarHeader-label {
            color: #111827 !important;
            font-weight: 600 !important;
          }
          
          /* Focus states for inputs */
          .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #00C853 !important;
          }
        `}
      </style>
      <Box sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        flex: 1,
        width: '100%',
      }}>
      {/* Search Input */}
      <TextField
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(e) => onSearchChange?.(e.target.value)}
        variant="outlined"
        size="small"
        sx={{
          flex: 1,
          minWidth: { xs: '100%', sm: 220 },
          '& .MuiOutlinedInput-root': {
            height: 40,
            borderRadius: 3,
            bgcolor: 'white',
            '&:hover fieldset': {
              borderColor: '#00C853',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00C853',
              boxShadow: '0 0 0 3px rgba(0, 200, 83, 0.1)',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: 14,
            padding: '10px 12px 10px 36px',
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ position: 'absolute', left: 12, pointerEvents: 'none' }}>
              <Search sx={{ color: '#6b7280', fontSize: 16 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Date Picker Button */}
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
        startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
        sx={{
          height: 40,
          minWidth: { xs: '100%', sm: 220 },
          width: { xs: '100%', sm: 'auto' },
          justifyContent: 'space-between',
          borderRadius: 3,
          borderColor: '#e5e7eb',
          color: '#111827',
          bgcolor: 'white',
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 500,
          '&:hover': {
            borderColor: '#00C853',
            bgcolor: '#f0fdf4',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getDisplayText()}
        </Box>
      </Button>

      {/* Clear Button */}
      <Button
        variant="outlined"
        onClick={handleClear}
        disabled={!dateFrom && !dateTo && !searchValue}
        startIcon={<Clear sx={{ fontSize: 16 }} />}
        sx={{
          height: 40,
          minWidth: { xs: '100%', sm: 100 },
          width: { xs: '100%', sm: 'auto' },
          borderRadius: 3,
          borderColor: '#e5e7eb',
          color: '#374151',
          bgcolor: 'white',
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 500,
          '&:hover': {
            bgcolor: '#f9fafb',
            borderColor: '#d1d5db',
          },
          '&:disabled': {
            color: '#9ca3af',
            borderColor: '#e5e7eb',
          }
        }}
      >
        {t('datePicker.clear', 'Clear')}
      </Button>

      {/* Date Picker Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            overflow: 'visible',
            borderRadius: 3,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          }
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('datePicker.selectDateRange', 'Select Date Range')}
          </Typography>

          {/* Preset Ranges as Chips */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: 13 }}>
              {t('datePicker.quickSelect', 'Quick Select')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {presetRanges.map((preset) => (
                <Chip
                  key={preset.id}
                  label={preset.label}
                  variant={selectedPreset === preset.id ? 'filled' : 'outlined'}
                  color={selectedPreset === preset.id ? 'primary' : 'default'}
                  onClick={() => handlePresetSelect(preset)}
                  sx={{
                    fontSize: 13,
                    height: 28,
                    borderRadius: 6,
                    borderColor: selectedPreset === preset.id ? '#00C853' : '#e5e7eb',
                    backgroundColor: selectedPreset === preset.id ? '#00C853' : 'white',
                    color: selectedPreset === preset.id ? 'white' : '#6b7280',
                    '&:hover': {
                      backgroundColor: selectedPreset === preset.id ? '#00A843' : '#f0fdf4',
                      borderColor: '#00C853',
                      color: selectedPreset === preset.id ? 'white' : '#00C853',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Custom Range */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: 13 }}>
              {t('datePicker.customRange', 'Custom Range')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('datePicker.from', 'From')}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      setCalendarFromAnchor(e.currentTarget);
                      setCalendarFromOpen(true);
                    }}
                    startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
                    fullWidth
                    sx={{
                      height: 40,
                      justifyContent: 'flex-start',
                      borderRadius: 2,
                      borderColor: '#e5e7eb',
                      color: customDateFrom ? '#111827' : '#6b7280',
                      bgcolor: 'white',
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#00C853',
                        bgcolor: '#f0fdf4',
                      }
                    }}
                  >
                    {customDateFrom ? (() => {
                      const date = new Date(customDateFrom);
                      return date.toLocaleDateString('uk-UA', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })() : t('datePicker.selectDate', 'Select date')}
                  </Button>
                  
                  <Popover
                    open={calendarFromOpen}
                    anchorEl={calendarFromAnchor}
                    onClose={() => setCalendarFromOpen(false)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <CustomCalendar
                      value={customDateFrom}
                      onChange={(date) => {
                        setCustomDateFrom(date);
                        setCalendarFromOpen(false);
                      }}
                      onClose={() => setCalendarFromOpen(false)}
                    />
                  </Popover>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('datePicker.to', 'To')}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      setCalendarToAnchor(e.currentTarget);
                      setCalendarToOpen(true);
                    }}
                    startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
                    fullWidth
                    sx={{
                      height: 40,
                      justifyContent: 'flex-start',
                      borderRadius: 2,
                      borderColor: '#e5e7eb',
                      color: customDateTo ? '#111827' : '#6b7280',
                      bgcolor: 'white',
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#00C853',
                        bgcolor: '#f0fdf4',
                      }
                    }}
                  >
                    {customDateTo ? (() => {
                      const date = new Date(customDateTo);
                      return date.toLocaleDateString('uk-UA', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                    })() : t('datePicker.selectDate', 'Select date')}
                  </Button>
                  
                  <Popover
                    open={calendarToOpen}
                    anchorEl={calendarToAnchor}
                    onClose={() => setCalendarToOpen(false)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <CustomCalendar
                      value={customDateTo}
                      onChange={(date) => {
                        setCustomDateTo(date);
                        setCalendarToOpen(false);
                      }}
                      onClose={() => setCalendarToOpen(false)}
                    />
                  </Popover>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 2, borderTop: '1px solid #f3f4f6' }}>
            <Button 
              variant="outlined" 
              onClick={handleClear}
              disabled={!dateFrom && !dateTo}
              sx={{
                borderColor: '#e5e7eb',
                color: '#6b7280',
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  borderColor: '#d1d5db',
                },
                '&:disabled': {
                  borderColor: '#e5e7eb',
                  color: '#9ca3af',
                }
              }}
            >
              {t('datePicker.clear', 'Clear')}
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={handleClose}
                sx={{
                  borderColor: '#e5e7eb',
                  color: '#374151',
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: '#f9fafb',
                    borderColor: '#d1d5db',
                  }
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCustomApply}
                disabled={!customDateFrom && !customDateTo}
                sx={{
                  bgcolor: '#00C853',
                  color: 'white',
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#00A843',
                    color: 'white',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    bgcolor: '#e5e7eb',
                    color: '#9ca3af',
                  }
                }}
              >
                {t('common.apply', 'Apply')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popover>
      </Box>
    </>
  );
};

export default HistoryFiltersPanel;
